import classnames from 'classnames';
import { isArray, isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { Icon } from '../../../shared/icon/Icon';
import { Delete, Tick } from '../../../shared/icons';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { OverflowTooltip } from '../../../shared/tooltip';
import { MLDataValue, MLObject } from '../../store/mlTypes';
import styles from './MLDataModal.module.scss';

const { green, red } = styles;

// CDEA Address Details Sort Order
const cdeaAddressKeys = ['partyATradingAddress', 'partyBTradingAddress', 'partyADocumentationAddress', 'partyBDocumentationAddress', 'partyANoticesAddress', 'partyBNoticesAddress'];
const potentialCdeaKeysOrder = ['Name', 'Name of Person', 'name', 'Address', 'address', 'FaxNo', 'Fax No', 'Fax No.', 'Fax', 'Fax Number', 'faxNo', 'fax', 'faxNumber', 'Email', 'E mail', 'E-mail', 'email'];

interface MLAnswerProps {
    answer: MLDataValue;
    depth: number;
    question?: string;
    includeDatasetField?: boolean;
    queryKey?: string;
}

export const MLAnswer: React.FC<MLAnswerProps> = ({ answer, depth, question, includeDatasetField, queryKey = '' }) => {
    const { color, icon } = useMemo(() => answer ? { icon: Tick, color: green } : { icon: Delete, color: red }, [answer]);

    const sortObjectKeys = useCallback((keyValuePairs: [string, MLDataValue][]) => {
        if (cdeaAddressKeys.includes(queryKey)) {
            return keyValuePairs.sort((a, b) => potentialCdeaKeysOrder.indexOf(a[0]) - potentialCdeaKeysOrder.indexOf(b[0]));
        }
        return keyValuePairs.sort((a, b) => a[0].localeCompare(b[0]));
    }, [queryKey]);

    const mlAnswerStyle = useMemo(() => {
        if (depth > 1) {
            return { width: '100%' };
        }
        if (!isUndefined(includeDatasetField)) {
            return includeDatasetField ? { width: 'calc(30% - 10px)', marginRight: '10px', justifyContent: 'center', alignItems: 'center' } : { width: '40%', alignItems: 'flex-end' };
        }
        return { width: 'fit-content', maxWidth: 'calc(100% - 150px)', marginRight: '10px' };
    }, [depth, includeDatasetField]);

    const aiAnswerLeftBlank = answer === '' && question && question.includes('leave this field blank');
    const returnNotFound = isUndefined(answer) || isNull(answer) || (answer === '' && question && !question.includes('leave this field blank'));

    if (returnNotFound) {
        return (
            <div className={classnames(styles.mlUnknownAnswer, { [styles.rightAlignText]: !includeDatasetField })} style={mlAnswerStyle}>
                <OverflowTooltip overlayText='Not Found by AI' />
            </div>
        );
    }

    if (aiAnswerLeftBlank) {
        return (
            <div className={classnames(styles.mlFieldLeftBlank, { [styles.rightAlignText]: !includeDatasetField })} style={mlAnswerStyle}>
                <OverflowTooltip overlayText='Left Blank by AI' />
            </div>
        );
    }

    if (typeof answer === 'boolean') {
        return (
            <div className={classnames(styles.mlDatasetAnswer, { [styles.rightAlignText]: !includeDatasetField })} style={mlAnswerStyle} >
                <Icon icon={icon} color={color} fontSize={20} />
            </div>
        );
    }

    if (['string', 'number'].includes(typeof answer)) {
        const overlayText = typeof answer === 'string' ? answer : (answer as number).toString();
        return (
            <div className={classnames(styles.mlDatasetAnswer, { [styles.rightAlignText]: !includeDatasetField })} style={mlAnswerStyle}>
                <div style={{ width: '100%', textAlign: 'center' }}>
                    <OverflowTooltip overlayText={overlayText} />
                </div>
            </div>
        );
    }

    if (isArray(answer)) {
        const isStringArray = typeof answer[0] === 'string';
        if (isStringArray) {
            return (
                <div className={classnames(styles.mlDatasetAnswer, { [styles.rightAlignText]: !includeDatasetField })} style={mlAnswerStyle}>
                    <div style={{ width: '100%', textAlign: 'center' }}>
                        <OverflowTooltip overlayText={answer.join(', ')} />
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.mlDatasetArrayObject} style={mlAnswerStyle}>
                {(answer as MLObject[]).map((row, i) => (
                    <div key={i} className={styles.mlDatasetObjectTable} style={{ width: '100%' }}>
                        {sortObjectKeys(Object.entries(row)).map(([key, value]) => (
                            <div className={styles.mlDatasetObjectRow} key={key}>
                                <div className={styles.mlDatasetObjectKey}><OverflowTooltip overlayText={key} /></div>
                                <div className={styles.mlDatasetObjectValue}>
                                    <Scrollable maxHeight='150px'>
                                        <MLAnswer answer={value} depth={depth + 1} question={question} includeDatasetField={includeDatasetField} queryKey={queryKey} />
                                    </Scrollable>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className={styles.mlDatasetObjectTable} style={mlAnswerStyle}>
            {sortObjectKeys(Object.entries(answer as MLObject)).map(([key, value]) => (
                <div className={styles.mlDatasetObjectRow} key={key}>
                    <div className={styles.mlDatasetObjectKey}><OverflowTooltip overlayText={key} /></div>
                    <div className={styles.mlDatasetObjectValue}>
                        <Scrollable maxHeight='150px'>
                            <MLAnswer answer={value} depth={depth + 1} question={question} includeDatasetField={includeDatasetField} queryKey={queryKey} />
                        </Scrollable>
                    </div>
                </div>
            ))}
        </div>
    );
};
